/**
 * ChequeSettings — Batch #14.334 : réglages d'impression des chèques fournisseurs (séquence, format 3 par page / 1 par page,
 * position haut · centre · bas, talon, format de date, décalages mm) + page de test d'alignement.
 * Batch #14.336 — éditeur de gabarit : chaque élément (date, bénéficiaire, montant, lettres, note, adresse) se place à la main.
 */
import { useState } from "react";
import { LayoutTemplate, Printer } from "lucide-react";
import { Btn, Field, downloadPdf, inputCls, useGlBase } from "./_shared";
import ChequeLayoutEditor from "./ChequeLayoutEditor";

export const CHEQUE_POSITIONS = [["top", "Haut"], ["middle", "Centre"], ["bottom", "Bas"]];
export const CHEQUE_LAYOUTS = [["3up", "3 chèques par feuille (8½ × 11, sans talon)"], ["1up", "1 chèque par feuille (avec ou sans talon)"]];
export const CHEQUE_STUBS = [["none", "Aucun talon"], ["bottom", "Talon en bas (chèque en haut)"], ["top", "Talon en haut (chèque en bas)"]];
const DATE_FORMATS = [["YYYY-MM-DD", "AAAA-MM-JJ (2026-06-21)"], ["DD/MM/YYYY", "JJ/MM/AAAA (21/06/2026)"], ["MM/DD/YYYY", "MM/JJ/AAAA (06/21/2026)"]];

export default function ChequeSettings({ value, onChange }) {
  const base = useGlBase();
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(false);
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const oneUp = v.layout === "1up";
  const stub = oneUp ? (v.stub || "none") : "none";
  const positions = CHEQUE_POSITIONS.filter(([k]) => !(stub === "top" && k === "top") && !(stub === "bottom" && k === "bottom"));
  const position = positions.some(([k]) => k === (v.position || "top")) ? (v.position || "top") : positions[0][0];
  return (
    <div className="space-y-3" data-testid="cheque-settings">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Prochain n° de chèque" hint="Attribué au moment d'imprimer (jamais avant).">
          <input type="number" min="1" value={v.next_no ?? 1} onChange={(e) => set("next_no", e.target.value)} className={`${inputCls} font-mono`} data-testid="cheque-next-no" />
        </Field>
        <Field label="Format du chèque" className="col-span-2 sm:col-span-3">
          <select value={v.layout || "3up"} onChange={(e) => set("layout", e.target.value)} className={inputCls} data-testid="cheque-layout">
            {CHEQUE_LAYOUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label={oneUp ? "Position du chèque" : "Position du prochain chèque"} hint={oneUp ? "Case de la feuille occupée par le chèque." : "Case où commence l'impression : utile quand il ne reste que 1 ou 2 chèques sur la feuille."}>
          <select value={position} onChange={(e) => set("position", e.target.value)} className={inputCls} data-testid="cheque-position">
            {positions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Talon" hint={oneUp ? "Fournisseur, date, n° et liste des factures payées." : "Pas de talon sur les feuilles de 3 chèques."}>
          <select value={stub} onChange={(e) => { const st = e.target.value; const pos = st === "top" && (v.position || "top") === "top" ? "bottom" : st === "bottom" && v.position === "bottom" ? "top" : (v.position || "top"); onChange({ ...v, stub: st, position: pos }); }} disabled={!oneUp} className={inputCls} data-testid="cheque-stub">
            {CHEQUE_STUBS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Date imprimée">
          <select value={v.date_format || "YYYY-MM-DD"} onChange={(e) => set("date_format", e.target.value)} className={inputCls} data-testid="cheque-date-format">
            {DATE_FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Décalage X (mm)" hint="+ = vers la droite"><input type="number" step="0.5" min="-30" max="30" value={v.offset_x_mm ?? 0} onChange={(e) => set("offset_x_mm", e.target.value)} className={`${inputCls} font-mono`} data-testid="cheque-offset-x" /></Field>
          <Field label="Décalage Y (mm)" hint="+ = vers le bas"><input type="number" step="0.5" min="-30" max="30" value={v.offset_y_mm ?? 0} onChange={(e) => set("offset_y_mm", e.target.value)} className={`${inputCls} font-mono`} data-testid="cheque-offset-y" /></Field>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
        <Btn tone={editor ? "sky" : "slate"} icon={LayoutTemplate} onClick={() => setEditor((x) => !x)} testId="cheque-layout-toggle">{editor ? "Masquer le gabarit" : "Placer les éléments sur le chèque"}</Btn>
        <Btn tone="slate" icon={Printer} busy={busy} onClick={() => downloadPdf(`${base}/cheques/test.pdf`, "test_alignement_cheques.pdf", setBusy)} testId="cheque-test-page">Page de test d'alignement</Btn>
        <span>Enregistrez d'abord vos réglages, imprimez la page de test sur papier ordinaire (échelle 100 %), superposez-la à un chèque et ajustez.</span>
      </div>
      {editor && <ChequeLayoutEditor fields={v.fields} onChange={(fields) => set("fields", fields)} />}
    </div>
  );
}
